import { useCallback, useEffect, useRef, useState } from "react";
import Lottie, { type LottieRefCurrentProps } from "lottie-react";
import onaLoadingAnimation from "@/assets/animations/ona-loading.json";
import { cn } from "@/components/podkit/lib/cn";

type OnaLoadingProps = {
    /**
     * Whether the loading is complete. When true, plays the dropping animation
     * and triggers onComplete callback when finished.
     */
    isReady?: boolean;
    /** Callback when the dropping animation completes (ready for crossfade) */
    onComplete?: () => void;
    /** Size of the animation (default: md / 32px) */
    size?: "sm" | "md" | "lg";
    /** Animation speed multiplier (default: 0.7 for smoother lifting) */
    speed?: number;
    /** Additional CSS classes */
    className?: string;
};

const sizeClasses = {
    sm: "size-6", // 24px
    md: "size-8", // 32px
    lg: "size-12", // 48px
} as const;

// Frame segments from animation metadata (at 100fps)
// Lifting: 0 to 0.3s, Spinning: 0.3 to 1s, Dropping: 1 to 1.25s
const SEGMENTS = {
    LIFTING: [0, 30] as [number, number],
    SPINNING: [30, 100] as [number, number],
    DROPPING: [100, 125] as [number, number],
};



type AnimationPhase = "lifting" | "spinning" | "dropping" | "complete";

// Color filters that match content-primary
const FILTERS = {
    // Light mode: darken to match content-primary rgb(26,26,26)
    light: "brightness(0) saturate(100%) invert(10%) sepia(0%) saturate(0%) hue-rotate(0deg) brightness(102%) contrast(88%)",
    // Dark mode: brighten to match content-primary rgb(225,225,225)
    dark: "brightness(0) saturate(100%) invert(88%) sepia(0%) saturate(0%) hue-rotate(0deg) brightness(102%) contrast(88%)",
};

/**
 * OnaLoading displays a Lottie animation for loading states.
 *
 * Animation phases:
 * 1. Lifting: Ona rises up (plays once on mount)
 * 2. Spinning: Ona spins (loops until isReady=true)
 * 3. Dropping: Ona drops down (plays once when ready)
 * 4. Complete: Animation finished, onComplete called
 *
 * Use with AnimatedLoadingContainer for crossfade transitions.
 */
export const OnaLoading = ({
    isReady = false,
    onComplete,
    size = "md",
    speed = 0.7,
    className,
}: OnaLoadingProps) => {
    const lottieRef = useRef<LottieRefCurrentProps>(null);
    const [phase, setPhase] = useState<AnimationPhase>("lifting");
    const pendingReadyRef = useRef(false);

    // Track when isReady becomes true while spinning
    useEffect(() => {
        if (isReady && phase === "spinning") {
            pendingReadyRef.current = true;
        }
    }, [isReady, phase]);

    // Handle segment completion
    const handleComplete = useCallback(() => {
        const lottie = lottieRef.current;
        if (!lottie) return;

        switch (phase) {
            case "lifting":
                // Lifting complete, start spinning
                setPhase("spinning");
                lottie.playSegments(SEGMENTS.SPINNING, true);
                break;

            case "spinning":
                // Check if we should drop or continue spinning
                if (pendingReadyRef.current || isReady) {
                    pendingReadyRef.current = false;
                    setPhase("dropping");
                    lottie.playSegments(SEGMENTS.DROPPING, true);
                } else {
                    // Continue spinning
                    lottie.playSegments(SEGMENTS.SPINNING, true);
                }
                break;

            case "dropping":
                // Dropping complete, notify parent
                setPhase("complete");
                onComplete?.();
                break;
        }
    }, [phase, isReady, onComplete]);

    // Start lifting animation on mount and set speed
    useEffect(() => {
        const lottie = lottieRef.current;
        if (!lottie) return;

        lottie.setSpeed(speed);
        lottie.playSegments(SEGMENTS.LIFTING, true);
    }, [speed]);

    const getFilter = () => {
        const isDarkMode = document.documentElement.classList.contains("dark");
        return isDarkMode ? FILTERS.dark : FILTERS.light;
    };

    return (
        <div
            className={cn("relative overflow-visible", sizeClasses[size], className)}
            style={{ filter: getFilter() }}
            role="img"
            aria-label="Ona loading"
        >
            <Lottie
                lottieRef={lottieRef}
                animationData={onaLoadingAnimation}
                loop={false}
                autoplay={false}
                onComplete={handleComplete}
                className="size-full overflow-visible"
                rendererSettings={{
                    preserveAspectRatio: "xMidYMid meet",
                }}
            />
        </div>
    );
};
